/*
 * File: floating-alert.ts
 * File Created: 2024-01-02 09:31:13
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-10-18 11:06:12
 * Modified By: Val Liu
 * -----
 */

import { VariantType } from "@root/components/base-classes";
import { ObjectHelper } from "@root/core/utilities/object-helper";
import { Vue, Component } from "vue-property-decorator";

interface IFloatingAlert {
    id: number;
    message: string;
    type: VariantType;
}

type IFloatingAlertAdd = Omit<IFloatingAlert, "id" | "type"> &
    Partial<Pick<IFloatingAlert, "id" | "type">>;
type IFloatingAlertSet = Omit<IFloatingAlert, "type"> &
    Partial<Pick<IFloatingAlert, "type">>;

const defaultType: VariantType = "info";

@Component
export default class FloatingAlert extends Vue {
    alerts: IFloatingAlert[] = [];
    nextId = 0;
    timeouts: { [key: number]: number } = {};

    addAlert(obj: IFloatingAlertAdd): IFloatingAlert {
        const id = obj.id ?? this.nextId++;
        const alert: IFloatingAlert = {
            id,
            type: defaultType,
            ...obj,
        };
        this.alerts.push(alert);
        /// register dismiss
        registerDismiss.call(this, id);

        return ObjectHelper.deepClone(alert);
    }

    setAlert(obj: IFloatingAlertSet): IFloatingAlert {
        obj.type = obj.type ?? defaultType;
        const { id, type, message } = obj;

        const index = this.alerts.findIndex((a) => a.id === id);
        /// if the id exists, change the type and message.
        if (index >= 0 && index < this.alerts.length) {
            const alert = this.alerts[index];
            alert.type = type ?? alert.type;
            alert.message = message ?? alert.message;
            /// register dismiss
            registerDismiss.call(this, id);
            return ObjectHelper.deepClone(alert);
        } else {
            /// else, add a new one.
            return this.addAlert(obj);
        }
    }

    dismissAlert(id: IFloatingAlert["id"]) {
        const index = this.alerts.findIndex((a) => a.id === id);
        if (index >= 0 && index < this.alerts.length) {
            const id = this.alerts[index].id;
            registerClear.call(this, id);
            this.alerts.splice(index, 1);
        }
    }
}

function registerClear(this: FloatingAlert, id: IFloatingAlert["id"]) {
    if (this.timeouts[id] != null) {
        clearTimeout(this.timeouts[id]);
        delete this.timeouts[id];
    }
}
function registerDismiss(this: FloatingAlert, id: IFloatingAlert["id"]) {
    registerClear.call(this, id);

    // Auto-dismiss after a certain time
    this.timeouts[id] = window.setTimeout(() => {
        this.dismissAlert(id);
        delete this.timeouts[id];
    }, 10000);
}
